import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  VStack,
  HStack,
  Text,
  Spinner,
  Progress,
  Box,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiCpu, FiClock } from 'react-icons/fi';

interface AILoadingModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  progress?: number;
  estimatedTime?: number; // in seconds
}

const AILoadingModal: React.FC<AILoadingModalProps> = ({
  isOpen,
  onClose,
  title = 'AI generuje obsah',
  message = 'Pracuji na vašem požadavku...',
  progress,
  estimatedTime,
}) => {
  const bg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const progressColor = useColorModeValue('blue.500', 'blue.300');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const getProgressMessage = () => {
    if (progress !== undefined) {
      if (progress < 25) return 'AI analyzuje požadavek...';
      if (progress < 50) return 'AI komunikuje s modelem...';
      if (progress < 75) return 'AI zpracovává obsah...';
      if (progress < 90) return 'AI optimalizuje výstup...';
      return 'AI dokončuje...';
    }
    return message;
  };

  const getTimeRemaining = () => {
    if (estimatedTime && progress !== undefined && progress > 0) {
      const remaining = Math.ceil((estimatedTime * (100 - progress)) / 100);
      if (remaining > 0) {
        return `Předpokládaný čas: ${remaining}s`;
      }
    }
    if (estimatedTime) {
      return `Předpokládaný čas: ${estimatedTime}s`;
    }
    return '';
  };

  const getRoundedProgress = () => {
    if (progress !== undefined) {
      return Math.round(progress);
    }
    return 0;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered closeOnEsc={false} closeOnOverlayClick={false}>
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(6px)" />
      <ModalContent bg={bg} borderRadius="2xl" boxShadow="2xl" border="1px solid" borderColor={borderColor}>
        <ModalHeader borderBottom="1px solid" borderColor={borderColor} pb={4}>
          <VStack spacing={3} align="center">
            <HStack spacing={3}>
              <Box bg="blue.50" p={2} borderRadius="full">
                <Icon as={FiCpu} color="blue.500" boxSize={5} />
              </Box>
              <Text fontSize="lg" fontWeight="600" color={textColor}>
                {title}
              </Text>
            </HStack>
          </VStack>
        </ModalHeader>
        
        <ModalBody py={8}>
          <VStack spacing={6} align="center">
            {/* Single centered AI Animation with model logo */}
            <VStack spacing={4}>
              <Box bg="blue.50" p={4} borderRadius="full">
                <Icon as={FiCpu} color="blue.500" boxSize={6} />
              </Box>
              <Spinner size="lg" color="blue.500" thickness="3px" />
            </VStack>

            {/* Status Message */}
            <Text fontSize="md" color={textColor} textAlign="center" fontWeight="500">
              {getProgressMessage()}
            </Text>

            {/* Progress Bar */}
            {progress !== undefined && (
              <Box w="100%" maxW="400px">
                <Progress
                  value={progress}
                  size="lg"
                  colorScheme="blue"
                  borderRadius="full"
                  bg="gray.100"
                  sx={{
                    '& > div': {
                      bg: progressColor,
                      transition: 'width 0.4s ease-out',
                    },
                  }}
                />
                <Text fontSize="sm" color="gray.600" mt={3} textAlign="center" fontWeight="500">
                  {getRoundedProgress()}% hotovo
                </Text>
              </Box>
            )}

            {/* Time Estimate */}
            {getTimeRemaining() && (
              <HStack spacing={2} color="gray.500">
                <Icon as={FiClock} boxSize={4} />
                <Text fontSize="sm">{getTimeRemaining()}</Text>
              </HStack>
            )}

            {/* Additional Info */}
            <VStack spacing={2} align="center" pt={2}>
              <Text fontSize="sm" color="gray.400" fontStyle="italic">
                AI pracuje na vašem obsahu
              </Text>
              <Text fontSize="xs" color="gray.400">
                Vygenerovaný obsah bude automaticky vložen do formuláře
              </Text>
            </VStack>
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor={borderColor}>
          <Text fontSize="sm" color="gray.400">
            Prosím čekejte, proces nelze přerušit
          </Text>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AILoadingModal;
